import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'
import KeyIcon from '@mui/icons-material/VpnKey'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Paper from '@mui/material/Paper'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { ApiError, makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'

type Role = 'admin' | 'dispatcher' | 'viewer'
const ALL_ROLES: Role[] = ['admin', 'dispatcher', 'viewer']

type AdminUser = {
  id: string
  email: string
  display_name: string
  roles: Role[]
  status: 'active' | 'disabled'
  last_login_at?: string | null
  password_updated_at?: string | null
  must_reset_password: boolean
  created_at: string
}

type Client = ReturnType<typeof makeClient>

const QUERY_KEY = ['admin', 'users'] as const

export default function AdminUsersPage() {
  const { accessToken } = useAuth()
  const client = useMemo(() => makeClient(() => accessToken), [accessToken])
  const qc = useQueryClient()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [resetting, setResetting] = useState<AdminUser | null>(null)
  const [deactivating, setDeactivating] = useState<AdminUser | null>(null)

  const usersQ = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data } = await client.callEnvelope<AdminUser[]>('/api/v1/admin/users')
      return data ?? []
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: QUERY_KEY })

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
            Users
          </Typography>
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            Add user
          </Button>
        </Stack>

          {usersQ.isLoading && <Typography variant="body2">Loading…</Typography>}
          {usersQ.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {usersQ.error instanceof Error ? usersQ.error.message : 'Failed to load users.'}
            </Alert>
          )}
        {usersQ.data && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Display name</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last login</TableCell>
                  <TableCell>Password updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersQ.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                      No users found.
                    </TableCell>
                  </TableRow>
                )}
                {usersQ.data.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {u.email}
                      </Typography>
                      {u.must_reset_password && (
                        <Chip label="Must reset" size="small" color="warning" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>{u.display_name}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {u.roles.map((r) => (
                          <Chip key={r} label={r} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={u.status}
                        size="small"
                        color={u.status === 'active' ? 'success' : 'default'}
                        variant={u.status === 'active' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{formatWhen(u.last_login_at)}</TableCell>
                    <TableCell>{formatWhen(u.password_updated_at)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setEditing(u)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reset password">
                          <IconButton size="small" onClick={() => setResetting(u)}>
                            <KeyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {u.status === 'active' && (
                          <Tooltip title="Deactivate">
                            <IconButton size="small" onClick={() => setDeactivating(u)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {creating && (
        <CreateUserDialog
          open
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); invalidate() }}
          client={client}
        />
      )}
      {editing && (
        <EditUserDialog
          open
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate() }}
          client={client}
        />
      )}
      {resetting && (
        <ResetPasswordDialog
          open
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => { setResetting(null); invalidate() }}
          client={client}
        />
      )}
      {deactivating && (
        <DeactivateDialog
          open
          user={deactivating}
          onClose={() => setDeactivating(null)}
          onDone={() => { setDeactivating(null); invalidate() }}
          client={client}
        />
      )}
    </>
  )
}

// --- Create dialog ---------------------------------------------------------

function CreateUserDialog({ open, onClose, onCreated, client }: {
  open: boolean; onClose: () => void; onCreated: () => void; client: Client
}) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [roles, setRoles] = useState<Role[]>(['viewer'])
  const [initialPassword, setInitialPassword] = useState('')

  const mut = useMutation({
    mutationFn: async () =>
      client.call<AdminUser>('/api/v1/admin/users', {
        method: 'POST',
        body: {
          email: email.trim(),
          display_name: displayName.trim(),
          roles,
          initial_password: initialPassword || undefined,
          send_reset_email: !initialPassword,
        },
      }),
    onSuccess: onCreated,
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add user</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Email" type="email" fullWidth required value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField label="Display name" fullWidth required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          <RoleSelect value={roles} onChange={setRoles} />
          <TextField
            label="Initial password (optional)"
            type="password"
            fullWidth
            value={initialPassword}
            onChange={(e) => setInitialPassword(e.target.value)}
            helperText={initialPassword
              ? 'User can sign in immediately with this password.'
              : 'Leave empty to force the user to set their password via a reset link.'}
          />
          {mut.error && (
            <Alert severity="error">{mut.error instanceof ApiError ? mut.error.message : 'Create failed.'}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={mut.isPending || !email || !displayName || roles.length === 0}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Creating…' : 'Create user'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// --- Edit dialog -----------------------------------------------------------

function EditUserDialog({ open, user, onClose, onSaved, client }: {
  open: boolean; user: AdminUser; onClose: () => void; onSaved: () => void; client: Client
}) {
  const [displayName, setDisplayName] = useState(user.display_name)
  const [roles, setRoles] = useState<Role[]>(user.roles)
  const [status, setStatus] = useState<'active' | 'disabled'>(user.status)

  const mut = useMutation({
    mutationFn: async () =>
      client.call<AdminUser>(`/api/v1/admin/users/${user.id}`, {
        method: 'PATCH',
        body: {
          display_name: displayName.trim(),
          roles,
          status,
        },
      }),
    onSuccess: onSaved,
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit user</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Email" value={user.email} disabled fullWidth />
          <TextField label="Display name" fullWidth required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          <RoleSelect value={roles} onChange={setRoles} />
          <FormControl fullWidth>
            <InputLabel id="status-lbl">Status</InputLabel>
            <Select
              labelId="status-lbl"
              value={status}
              label="Status"
              input={<OutlinedInput label="Status" />}
              onChange={(e: SelectChangeEvent) => setStatus(e.target.value as 'active' | 'disabled')}
            >
              <MenuItem value="active">active</MenuItem>
              <MenuItem value="disabled">disabled</MenuItem>
            </Select>
          </FormControl>
          {mut.error && (
            <Alert severity="error">{mut.error instanceof ApiError ? mut.error.message : 'Save failed.'}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={mut.isPending || !displayName}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// --- Reset password dialog -------------------------------------------------

function ResetPasswordDialog({ open, user, onClose, onDone, client }: {
  open: boolean; user: AdminUser; onClose: () => void; onDone: () => void; client: Client
}) {
  type Result = { message: string; reset_url?: string; expires_in_seconds?: number }
  const mut = useMutation({
    mutationFn: async () =>
      client.call<Result>(`/api/v1/admin/users/${user.id}/reset-password`, { method: 'POST' }),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reset password for {user.email}</DialogTitle>
      <DialogContent>
        {mut.data ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="success">{mut.data.message}</Alert>
            {mut.data.reset_url && (
              <Alert severity="info">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Dev-mode link (copy and share):</strong>
                </Typography>
                <Link component={RouterLink} to={mut.data.reset_url}>
                  {mut.data.reset_url}
                </Link>
                {mut.data.expires_in_seconds !== undefined && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    Expires in {Math.round(mut.data.expires_in_seconds / 60)} minutes.
                  </Typography>
                )}
              </Alert>
            )}
          </Stack>
        ) : (
          <DialogContentText>
            This issues a new password reset token for <b>{user.email}</b> and forces the user to set a new
            password on next sign-in. Any previous unused reset tokens are revoked.
          </DialogContentText>
        )}
        {mut.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {mut.error instanceof ApiError ? mut.error.message : 'Reset failed.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {mut.data ? (
          <Button variant="contained" color="secondary" onClick={onDone}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" color="secondary" disabled={mut.isPending} onClick={() => mut.mutate()}>
              {mut.isPending ? 'Issuing…' : 'Issue reset token'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

// --- Deactivate dialog -----------------------------------------------------

function DeactivateDialog({ open, user, onClose, onDone, client }: {
  open: boolean; user: AdminUser; onClose: () => void; onDone: () => void; client: Client
}) {
  const mut = useMutation({
    mutationFn: async () =>
      client.call<AdminUser>(`/api/v1/admin/users/${user.id}`, { method: 'DELETE' }),
    onSuccess: onDone,
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Deactivate {user.email}?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This flips the user's status to <b>disabled</b>. They can't sign in, but the record stays for the
          audit trail. You can re-activate them by editing status back to <b>active</b>.
        </DialogContentText>
        {mut.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {mut.error instanceof ApiError ? mut.error.message : 'Deactivate failed.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Deactivating…' : 'Deactivate'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// --- shared bits -----------------------------------------------------------

function RoleSelect({ value, onChange }: { value: Role[]; onChange: (r: Role[]) => void }) {
  return (
    <FormControl fullWidth required>
      <InputLabel id="roles-lbl">Roles</InputLabel>
      <Select<Role[]>
        labelId="roles-lbl"
        multiple
        value={value}
        input={<OutlinedInput label="Roles" />}
        onChange={(e: SelectChangeEvent<Role[]>) => {
          const v = e.target.value
          onChange(typeof v === 'string' ? (v.split(',') as Role[]) : v)
        }}
        renderValue={(selected) => (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
            {selected.map((r) => <Chip key={r} label={r} size="small" />)}
          </Stack>
        )}
      >
        {ALL_ROLES.map((r) => (
          <MenuItem key={r} value={r}>{r}</MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

function formatWhen(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return '—'
  }
}
